'use client';
import Link from 'next/link';
import Image from 'next/image';
import { FileLink } from '../../../../types/general';
import Tooltip from '../../shared/tooltip';

type ComponentProps = {
	link: FileLink;
};

export default function BlockLinkButton({ link }: ComponentProps) {
	// Function to determine if the URL starts with http:// or https://
	const isExternalUrl = (url: string) => /^(http|https):\/\//.test(url);

	// Determine the href value, handling both internal file links and external URLs
	let hrefValue = '#'; // Default to '#' if neither URL is present

	if (link.file?.url) {
		hrefValue = `${process.env.NEXT_PUBLIC_CMS_URL}${link.file.url}`;
	} else if (link.url) {
		// Check if URL is external, if not, prepend with 'http://'
		hrefValue = isExternalUrl(link.url) ? link.url : `http://${link.url}`;
	}

	// Determine the icon based on the link type and URL/file
	const determineIcon = () => {
		const url = link.url || link.file?.url || '';
		if (url.includes('.pdf')) {
			return (
				<div className="mr-2 flex h-5 max-h-5 w-5 max-w-[28px] flex-shrink md:h-6 md:max-h-6 md:w-5">
					<Image alt="Document icon" height="28" src="/vectors/document-icon-white.svg" width="28" />
				</div>
			);
		}
		if (url.includes('.png') || url.includes('.jpg') || url.includes('.jpeg')) {
			return (
				<div className="mr-2 flex h-5 max-h-5 w-5 max-w-[28px] flex-shrink md:h-6 md:max-h-6 md:w-5">
					<Image alt="Photo icon" height="28" src="/vectors/photo-icon-white.svg" width="28" />
				</div>
			);
		}
		return (
			<div className="mr-2 flex h-5 max-h-5 w-5 max-w-[28px] flex-shrink md:h-6 md:max-h-6 md:w-5">
				<Image alt="Arrow icon" height="28" src="/vectors/arrow-right-icon-white.svg" width="28" />
			</div>
		);
	};

	return (
		<Link
			className="w-full items-center text-center text-base"
			download={Boolean(link.file?.url)}
			href={hrefValue}
			rel="noopener noreferrer"
			target="_blank"
		>
			<Tooltip label={link.description}>
				{link.type === 'Text Link' ? (
					<span className=" text-UC-blue-700 hover:text-UC-blue-600 mr-2 w-full text-lg underline md:text-base">
						{link.name}
					</span>
				) : (
					<button
						className="bg-UC-blue-700 hover:bg-UC-blue-600 mr-2 h-12 w-full rounded-md px-3 py-1 font-medium text-white transition-all duration-300 ease-in-out lg:font-semibold"
						type="button"
					>
						<div className="flex items-center justify-center space-x-2 whitespace-nowrap">
							<div>{link.name}</div>
							{determineIcon()}
						</div>
					</button>
				)}
			</Tooltip>
		</Link>
	);
}
